import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Plus, X, Trash2, Loader2, Edit2, Sparkles } from "lucide-react";
import { notify, fmtError } from "@/lib/notify";
import DocAttachTrigger from "@/components/DocAttachTrigger";
import DocumentAttachZone from "@/components/DocumentAttachZone";
import DateFieldFr from "@/components/DateFieldFr";

// Affichage localisé JJ/MM/AAAA des valeurs date ISO (canonique conservé côté serveur)
const fmtCell = (v) =>
  typeof v === "string" && /^\d{4}-\d{2}-\d{2}(T[\d:.]+Z?)?$/.test(v)
    ? v.slice(0, 10).split("-").reverse().join("/")
    : v;

// Modules supportés pour l'attachement Noam Drive (Phase 2 cross-module).
// Si le `module.path` est dans ce set, on affiche le bouton 📎 + la zone
// d'attache dans la modale d'édition.
const DOC_ATTACH_MODULES = new Set([
  "plans", "incidents", "fournisseurs", "controles", "audits",
]);

// P2 — Modules supportant la lecture IA NOAM (Sparkles).
const SPARKLE_MODULES = new Set([
  "controles", "incidents", "fournisseurs", "audits",
]);

/**
 * Generic CRUD module for simple workspace collections (Contrôles, Incidents,
 * Alertes, Fournisseurs, Plans d'action, Filtres).
 *
 * Props:
 *   - module: { path, title, subtitle, overline, accent, icon, dataTestId }
 *   - fields: array of { key, label, type, options?, placeholder?, hidden?, required?, default?, render? }
 *   - columns: array of { key, label, render? } shown in the table
 *   - emptyMsg, primaryCta
 */
export default function GenericCrudModule({ module, fields, columns, emptyMsg, primaryCta, kpis, headerActions }) {
  const { t } = useTranslation();
  const Icon = module.icon;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [busy, setBusy] = useState(false);
  const initialForm = Object.fromEntries(fields.map((f) => [f.key, f.default ?? (f.type === "number" ? 0 : "")]));
  const [form, setForm] = useState(initialForm);

  const isEditing = editingRow !== null;
  const [aiBusy, setAiBusy] = useState(false);
  const [aiReading, setAiReading] = useState("");

  const runSparkle = async (row) => {
    setAiBusy(true);
    setAiReading("");
    try {
      const r = await api.post("/workspace/ai/sparkle", {
        module: module.path,
        entity_id: row.id,
      });
      setAiReading(r.data?.ai_reading || "");
    } catch (err) {
      notify.error(fmtError(err, "Lecture IA indisponible."));
    } finally { setAiBusy(false); }
  };

  const openEdit = (row) => {
    const prefilled = { ...initialForm };
    fields.forEach((f) => {
      if (row[f.key] !== undefined && row[f.key] !== null) prefilled[f.key] = row[f.key];
    });
    setForm(prefilled);
    setEditingRow(row);
  };

  const closeModal = () => { setShowAdd(false); setEditingRow(null); setForm(initialForm); setAiPrefilled(false); };

  const load = () =>
    api.get(`/workspace/${module.path}`).then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.detail || t("saas.crud.error")));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [module.path]);

  // V23.15.21 — Prefill par le Copilote IA (sessionStorage)
  // Le module.path est utilisé comme slug par défaut (ex: "plans" → noam_prefill_plans).
  // Le composant ouvre automatiquement la modale en mode create avec les champs préchargés.
  const [aiPrefilled, setAiPrefilled] = useState(false);
  useEffect(() => {
    try {
      const slug = module.prefillKey || module.path;
      if (!slug) return;
      const raw = sessionStorage.getItem(`noam_prefill_${slug}`);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      sessionStorage.removeItem(`noam_prefill_${slug}`);
      if (parsed && parsed.data && typeof parsed.data === "object") {
        // Filter to known fields only
        const allowed = new Set(fields.map((f) => f.key));
        const merged = { ...initialForm };
        for (const [k, v] of Object.entries(parsed.data)) {
          if (allowed.has(k)) merged[k] = v;
        }
        setForm(merged);
        setShowAdd(true);
        setAiPrefilled(true);
      }
    } catch (e) {
      // Silently ignore corrupted prefill
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module.path]);

  const submitAdd = async (e) => {
    e.preventDefault();
    // W15.4 — validation explicite : ne jamais échouer en silence.
    const missing = fields.filter((f) => f.required && !String(form[f.key] ?? "").trim()).map((f) => f.label);
    if (missing.length) return notify.error(`Champs obligatoires manquants : ${missing.join(", ")}.`);
    setBusy(true);
    try {
      const payload = { ...form };
      fields.forEach((f) => {
        if (f.type === "number") payload[f.key] = parseInt(payload[f.key] || 0, 10);
      });
      if (isEditing) {
        await api.patch(`/workspace/${module.path}/${editingRow.id}`, payload);
      } else {
        await api.post(`/workspace/${module.path}`, payload);
      }
      closeModal();
      await load();
    } catch (err) {
      notify.error(fmtError(err, t("saas.crud.error")));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("saas.crud.delete_confirm"))) return;
    try { await api.delete(`/workspace/${module.path}/${id}`); notify.success(t("saas.crud.deleted", { defaultValue: "Supprimé." })); await load(); }
    catch (err) { notify.error(fmtError(err, t("saas.crud.error"))); }
  };

  if (error) return <div className="text-[#E24B4A] text-sm">{error}</div>;
  if (!data) return <div className="text-zinc-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {t("saas.crud.loading")}</div>;

  return (
    <div className="space-y-6" data-testid={module.dataTestId}>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 noam-overline" style={{ color: module.accent }}>
            <Icon className="w-3 h-3" /> {module.overline}
          </div>
          <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">{module.title}</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-3xl">{module.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {headerActions}
          <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid={`${module.dataTestId}-add`}>
            <Plus className="w-4 h-4" /> {primaryCta}
          </button>
        </div>
      </div>

      {kpis && (
        <div className="grid md:grid-cols-4 gap-4">
          {kpis(data).map((k, i) => (
            <div key={i} className="noam-card p-5" data-testid={`${module.dataTestId}-kpi-${i}`}>
              <div className="text-xs text-zinc-400">{k.label}</div>
              <div className="noam-display text-3xl font-bold mt-3" style={{ color: k.color || "#fff" }}>{k.value}</div>
              {k.hint && <div className="text-[11px] text-zinc-500 mt-1">{k.hint}</div>}
            </div>
          ))}
        </div>
      )}

      {data.is_empty ? (
        <div className="noam-card p-12 text-center">
          <Icon className="w-12 h-12 mx-auto opacity-60" style={{ color: module.accent }} />
          <div className="noam-display text-xl mt-4">{t("saas.crud.no_items")}</div>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">{emptyMsg}</p>
          <button onClick={() => setShowAdd(true)} className="btn-primary mt-6 mx-auto inline-flex" data-testid={`${module.dataTestId}-empty-add`}>
            <Plus className="w-4 h-4" /> {primaryCta}
          </button>
        </div>
      ) : (
        <div className="noam-card overflow-hidden">
          <div className="px-5 py-4 border-b border-white/5">
            <div className="noam-overline">{data.total} {data.total > 1 ? t("saas.crud.items_label_plural") : t("saas.crud.items_label_singular")}</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 border-b border-white/5 bg-white/[0.02]">
                <tr>
                  {columns.map((c) => (<th key={c.key} className="text-left py-2.5 px-5 font-normal">{c.label}</th>))}
                  <th className="text-center font-normal w-12"></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <tr key={row.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    {columns.map((c) => (
                      <td key={c.key} className="py-3 px-5 text-zinc-200">
                        {c.render ? c.render(row) : (row[c.key] != null && row[c.key] !== "" ? fmtCell(row[c.key]) : "—")}
                      </td>
                    ))}
                    <td className="text-center">
                      <div className="inline-flex items-center gap-0.5">
                        {DOC_ATTACH_MODULES.has(module.path) && (
                          <DocAttachTrigger
                            module={module.path}
                            entityId={row.id}
                            entityLabel={row.titre || row.nom || row.label}
                            className="mr-1.5"
                          />
                        )}
                        {SPARKLE_MODULES.has(module.path) && (
                          <button
                            onClick={() => runSparkle(row)}
                            disabled={aiBusy}
                            className="text-zinc-500 hover:text-[#EF9F27] p-1 disabled:opacity-30"
                            data-testid={`${module.dataTestId}-sparkle-${row.id}`}
                            title="Lecture IA"
                          >
                            {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                          </button>
                        )}
                        <button onClick={() => openEdit(row)} className="text-zinc-500 hover:text-[#378ADD] p-1" data-testid={`${module.dataTestId}-edit-${row.id}`} title={t("saas.crud.edit", { defaultValue: "Modifier" })}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => remove(row.id)} className="text-zinc-500 hover:text-[#E24B4A] p-1" data-testid={`${module.dataTestId}-del-${row.id}`} title={t("saas.crud.delete", { defaultValue: "Supprimer" })}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(showAdd || isEditing) && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={closeModal}>
          <div className="noam-card w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={`${module.dataTestId}-modal`}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="noam-overline">{module.overline}</div>
                <div className="noam-display text-lg font-semibold">
                  {isEditing ? t("saas.crud.edit_title", { defaultValue: "Modifier" }) : primaryCta}
                </div>
              </div>
              <button onClick={closeModal} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitAdd} noValidate className="space-y-4">
              {aiPrefilled && !isEditing && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#EF9F27]/10 border border-[#EF9F27]/30 text-[11px] text-[#EF9F27]" data-testid={`${module.dataTestId}-prefill-banner`}>
                  <span>Pré-rempli par l&apos;IA Copilote — vérifiez et ajustez avant de créer.</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {fields.filter((f) => !f.hidden).map((f) => (
                  <div key={f.key} className={f.span === 2 ? "col-span-2" : ""}>
                    <label className="text-xs text-zinc-400 mb-1 block">{f.label}{f.required && <span className="text-[#E24B4A]"> *</span>}</label>
                    {f.type === "select" && (
                      <select required={f.required} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                        className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
                        data-testid={`${module.dataTestId}-field-${f.key}`}>
                        {f.options.map((o) => <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>{typeof o === "string" ? o : o.label}</option>)}
                      </select>
                    )}
                    {f.type === "textarea" && (
                      <textarea rows={3} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                        placeholder={f.placeholder}
                        className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white resize-none focus:border-[#378ADD] focus:outline-none"
                        data-testid={`${module.dataTestId}-field-${f.key}`} />
                    )}
                    {f.type === "date" && (
                      <DateFieldFr
                        value={form[f.key]}
                        onChange={(v) => setForm({ ...form, [f.key]: v })}
                        className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                        testid={`${module.dataTestId}-field-${f.key}`}
                      />
                    )}
                    {(!f.type || f.type === "text" || f.type === "number") && (
                      <input
                        type={f.type === "number" ? "number" : "text"}
                        required={f.required}
                        min={f.type === "number" ? 0 : undefined}
                        max={f.max}
                        value={form[f.key]}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                        placeholder={f.placeholder}
                        className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
                        data-testid={`${module.dataTestId}-field-${f.key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
              <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid={`${module.dataTestId}-submit`}>
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("saas.crud.saving")}</> : (isEditing ? t("saas.crud.save", { defaultValue: "Enregistrer" }) : primaryCta)}
              </button>
              {isEditing && editingRow?.id && DOC_ATTACH_MODULES.has(module.path) && (
                <div className="pt-3 border-t border-white/5">
                  <DocumentAttachZone
                    module={module.path}
                    entityId={editingRow.id}
                    entityLabel={editingRow.titre || editingRow.nom || editingRow.label}
                    compact
                  />
                </div>
              )}
            </form>
          </div>
        </div>
      )}
      {aiReading && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setAiReading("")}
          data-testid={`${module.dataTestId}-sparkle-result`}
        >
          <div className="noam-card max-w-2xl w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-[#EF9F27]" />
                <div className="noam-overline">Lecture IA · {module.title}</div>
              </div>
              <button onClick={() => setAiReading("")} className="text-zinc-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <pre className="whitespace-pre-wrap text-sm text-zinc-200 font-sans leading-relaxed max-h-[60vh] overflow-y-auto">{aiReading}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
